import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import type { HHPeriod } from '../../../services/dataService';
import { colorForIndex } from '../../../utils/colors';
import * as colors from '../../../../../common/styles/colors';
import { barThickness } from '../../../../../common/styles/global/chart.styles';
import { getCustomTooltipHandler } from './utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

type MultiSeries = Array<{ label: string; entries: HHPeriod[] }>;

interface HalfHourlyChartProps {
  mode?: 'single' | 'multi';
  data?: HHPeriod[];
  series?: MultiSeries;
  date?: string;
  selectedMpans: string[];
}

const HalfHourlyChart: React.FC<HalfHourlyChartProps> = ({
  mode = 'single',
  data = [],
  series = [],
  date,
  selectedMpans,
}) => {
  const hasHalfHourlyData = () => {
    if (mode === 'single') {
      if (Object.keys(data).length > 0 && Object.values(data).some(item => item.consumptionValue > 0)) {
        return true;
      }
      return false;
    } else {
      if (
        Object.keys(series).length > 0 &&
        Object.values(series).some(arr => arr.entries && arr.entries.some(item => item.consumptionValue > 0))
      ) {
        return true;
      }
      return false;
    }
  };

  const chartDataSingle = useMemo(() => {
    const labels = data.map(d => d.period.toString());

    const actual = data.map(d => (!d.isEstimated ? d.consumptionValue : null));
    const estimated = data.map(d => (d.isEstimated ? d.consumptionValue : null));

    return {
      labels,
      datasets: [
        {
          data: actual,
          skipNull: true,
          backgroundColor: colors.hydroBlue,
          borderColor: colors.hydroBlue,
          borderWidth: 1,
          maxBarThickness: barThickness,
        },
        {
          data: estimated,
          skipNull: true,
          backgroundColor: colors.draxDarkBlue,
          borderColor: colors.draxDarkBlue,
          borderWidth: 1,
          maxBarThickness: barThickness,
        },
      ],
    };
  }, [data]);

  const chartDataMulti = useMemo(() => {
    const labels = (series.find(s => s.entries?.length)?.entries || []).map(d => d.period.toString());
    const datasets = (series || []).map((s, i) => {
      const { stroke, fill } = colorForIndex(i);
      return {
        label: s.label,
        data: (s.entries || []).map(d => d.consumptionValue),
        borderColor: stroke,
        backgroundColor: fill,
        tension: 0.2,
        pointRadius: 2,
      };
    });
    return { labels, datasets };
  }, [series]);

  const options = useMemo(() => {
    let title = 'Half-hourly Consumption';
    if (date) {
      const d = new Date(date);
      const day = d.getDate().toString().padStart(2, '0');
      const month = d.toLocaleString(undefined, { month: 'long' });
      const year = d.getFullYear();
      title = `Half-hourly Consumption - ${day} ${month} ${year}`;

      if (series && series.length > 0) title = `MPAN Comparison - Half-hourly Consumption - ${day} ${month} ${year}`;

      if (selectedMpans && selectedMpans.length === 1)
        title = `${selectedMpans[0]} - Half-hourly Consumption - ${day} ${month} ${year}`;
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: title,
          font: {
            size: 16, // Set the font size in pixels
            weight: 400,
            family: `Rubrik, 'Calibri'`,
          },
          color: colors.hydroBlue,
        },
        tooltip: {
          // https://www.chartjs.org/docs/latest/configuration/tooltip.html
          enabled: false,
          mode: 'point',
          external: getCustomTooltipHandler('kWh'),
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'Half-hourly interval - 00:00 to 23:59', color: colors.hydroBlue },
          ticks: {
            autoskip: false,
            minRotation: 90,
            maxRotation: 90,
            color: colors.hydroBlue,
          },
          grid: { display: false },
        },
        y: {
          title: {
            display: true,
            text: 'kWh',
            color: colors.hydroBlue,
          },
          ticks: {
            color: colors.hydroBlue,
          },
          beginAtZero: true,
        },
      },
    };
  }, [data, series]);

  return (
    <div className='pl-3' style={{ height: 500 }}>
      {hasHalfHourlyData() ? (
        mode === 'single' ? (
          <Bar data={chartDataSingle} options={options as any} />
        ) : (
          <Line data={chartDataMulti} options={options as any} />
        )
      ) : (
        <div className='d-flex justify-content-center'>No data available for this period</div>
      )}
    </div>
  );
};

export default HalfHourlyChart;
